import { AppleLogoIcon } from "@/components/icons/AppleLogoIcon.tsx";
import { GoogleIcon } from "@/components/icons/GoogleIcon.tsx";
import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { MicrosoftIcon } from "@/components/icons/MicrosoftIcon.tsx";
import { SlackIcon } from "@/components/icons/SlackIcon.tsx";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import { passwordlessOptionsQuery } from "@/components/auth/passwordlessOptions.ts";
import { appleSignInOptionsQuery } from "@/shared/appleSignInOptions.ts";
import { slackSignInOptionsQuery } from "@/shared/slackSignInOptions.ts";
import { markConnectStarted } from "@/components/connectors/pendingConnect.ts";
import { APPLE_SIGN_IN_PATH } from "@/types/appleSignIn.ts";
import { SLACK_SIGN_IN_PATH } from "@/types/slackSignIn.ts";
import { pageColumnClasses } from "@/components/layout/contentLayout.tsx";
import { Button } from "@/components/ui/button.tsx";
import { dismissAllToasts, errorToast } from "@/components/ui/toast.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { queryClient } from "@/lib/query-client.ts";
import { cn, getApiUrl, resolveApiPath } from "@/lib/utils.ts";
import { isInAppPath } from "@/shared/appLink.ts";
import { connectErrorCopy } from "@/shared/connectErrorCopy.ts";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Phone } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

/**
 * Detects if the page is being viewed inside an in-app browser (WebView).
 * Google OAuth blocks sign-in from embedded browsers with a 403 disallowed_useragent
 * error, so we need to detect this and guide users to open in their system browser.
 */
function isInAppBrowser(): boolean {
  // Allow simulating in-app browser via ?inapp=1 for testing
  if (new URLSearchParams(window.location.search).has("inapp")) {
    return true;
  }

  const ua = navigator.userAgent || navigator.vendor || "";

  const inAppPatterns = [
    /\bLinkedIn/i,
    /\bFBAN\b/i, // Facebook
    /\bFBAV\b/i, // Facebook
    /\bInstagram/i,
    /\bTwitter/i,
    /\bSnapchat/i,
    /\bLine\//i,
    /\bKAKAOTALK/i,
    /\bMicroMessenger/i, // WeChat
    /\bWeChat/i,
  ];
  if (inAppPatterns.some((pattern) => pattern.test(ua))) {
    return true;
  }

  // iOS WebView: has iPhone/iPad/iPod but no "Safari/" (real Safari always includes it)
  if (/\b(iPhone|iPod|iPad)\b/i.test(ua) && !/Safari\//i.test(ua)) {
    return true;
  }
  // Android WebView: explicit markers
  if (/Android/i.test(ua) && /\bwv\b/.test(ua)) {
    return true;
  }

  return false;
}

/**
 * Builds the Google OAuth URL, including a returnTo parameter for deferred deep linking.
 * When an unauthenticated user lands on a protected route (e.g., /threads/123?foo=bar),
 * we capture that URL and pass it through the OAuth flow so they end up there after auth.
 */
function getLoginReturnTo(location: string, search: string): string | undefined {
  const searchParams = new URLSearchParams(search);
  if (location === "/auth") {
    return searchParams.get("returnTo") ?? undefined;
  }

  if (location !== "/" || search.length > 0) {
    return search ? `${location}${search}` : location;
  }

  return undefined;
}

function buildLoginAuthUrl(providerPath: string, location: string): string {
  const baseUrl = resolveApiPath(providerPath);
  const returnTo = getLoginReturnTo(location, window.location.search);
  if (!returnTo) return baseUrl;
  return `${baseUrl}?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * The path of a passwordless door, carrying the destination the sign-in page
 * was given, so finishing there lands where an OAuth sign-in would.
 */
function passwordlessDoorPath(doorPath: string, location: string): string {
  const returnTo = getLoginReturnTo(location, window.location.search);
  if (!returnTo) return doorPath;
  return `${doorPath}?returnTo=${encodeURIComponent(returnTo)}`;
}

export type LoginReturnNavigation =
  | { kind: "route"; path: string }
  | { kind: "external"; url: string };

type LoginReturnNavigationOptions = {
  appOrigin?: string;
  apiBaseUrl?: string;
};

type ApiReturnBase = {
  origin: string;
  path: string;
  allowsLocalDevOrigin: boolean;
};

function isLocalDevOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  } catch {
    return false;
  }
}

function getApiReturnBase(apiBaseUrl: string, appOrigin: string): ApiReturnBase {
  const url = new URL(apiBaseUrl, appOrigin);
  const usesRelativePath = apiBaseUrl.startsWith("/") && !apiBaseUrl.startsWith("//");
  return {
    origin: url.origin,
    path: url.pathname.replace(/\/+$/, ""),
    allowsLocalDevOrigin: usesRelativePath && isLocalDevOrigin(appOrigin),
  };
}

function isApiReturnUrl(url: URL, apiBase: ApiReturnBase): boolean {
  if (
    url.origin !== apiBase.origin &&
    !(apiBase.allowsLocalDevOrigin && isLocalDevOrigin(url.origin))
  ) {
    return false;
  }
  if (!apiBase.path) return true;
  return url.pathname === apiBase.path || url.pathname.startsWith(`${apiBase.path}/`);
}

export function resolveLoginReturnNavigation(
  returnTo: string | undefined | null,
  options: LoginReturnNavigationOptions = {},
): LoginReturnNavigation {
  if (!returnTo) return { kind: "route", path: "/" };
  // Every route this returns is handed unparsed to a full-page navigation on
  // the phone sign-in page, so it must reject the characters the browser strips
  // before it parses a URL, not just `//` and `\`. The shared guard is the one
  // check that does, and it keeps this resolver in step with every other
  // return-location consumer.
  if (isInAppPath(returnTo)) {
    return { kind: "route", path: returnTo };
  }

  const appOrigin = options.appOrigin ?? window.location.origin;
  const apiBase = getApiReturnBase(options.apiBaseUrl ?? getApiUrl(), appOrigin);

  try {
    const url = new URL(returnTo);
    if (isApiReturnUrl(url, apiBase)) {
      return { kind: "external", url: url.toString() };
    }
    if (url.origin === appOrigin) {
      // Parsing does not make the path safe: `https://app//evil.example` has our
      // origin and the pathname `//evil.example`, which is protocol-relative
      // once navigated to on its own. Only the pathname can carry that trick;
      // the parser has already fenced the query and fragment behind `?` and `#`,
      // so a backslash in either stays an ordinary deep-link character.
      if (!isInAppPath(url.pathname)) return { kind: "route", path: "/" };
      return { kind: "route", path: `${url.pathname}${url.search}${url.hash}` };
    }
  } catch {
    return { kind: "route", path: "/" };
  }

  return { kind: "route", path: "/" };
}

const useGoogleAuthUrl = () => {
  const [location] = useLocation();
  return buildLoginAuthUrl("/api/p/auth/google/base", location);
};

const useMicrosoftAuthUrl = () => {
  const [location] = useLocation();
  return buildLoginAuthUrl("/api/p/auth/microsoft/base", location);
};

const SignInButton: React.FC = () => {
  const authUrl = useGoogleAuthUrl();

  return (
    <Button
      asChild
      variant="popover"
      className="border-default h-14 px-8 py-2"
      data-statsig-name="auth-signin-google"
    >
      {/* Dismiss stale toasts (e.g. "Sign-in failed") so they don't persist
          through the OAuth flow and confuse the user on a successful return. */}
      <a href={authUrl} target="_blank" rel="noopener" className="gap-2" onClick={dismissAllToasts}>
        <GoogleIcon className="size-4" />
        <span className="text-base font-medium leading-none">Continue with Google</span>
      </a>
    </Button>
  );
};

/**
 * The round, icon-only shape of the doors after Google, Microsoft and Phone.
 * Each keeps its "Continue with …" label for screen readers, and shows it in a
 * tooltip.
 */
const iconDoorClasses = "border-default h-14 w-14";

/** Names an icon door in the app's tooltip, on hover or focus. */
const IconDoorTooltip: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    {/* Below, because above it would cover the "or continue with" divider. */}
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

const MicrosoftSignInButton: React.FC = () => {
  const authUrl = useMicrosoftAuthUrl();

  return (
    <Button
      asChild
      variant="popover"
      className="border-default h-14 px-8 py-2"
      data-statsig-name="auth-signin-microsoft"
    >
      {/* Dismiss stale toasts (e.g. "Sign-in failed") so they don't persist
          through the OAuth flow and confuse the user on a successful return. */}
      <a href={authUrl} target="_blank" rel="noopener" className="gap-2" onClick={dismissAllToasts}>
        <MicrosoftIcon className="size-4" />
        <span className="text-base font-medium leading-none">Continue with Microsoft</span>
      </a>
    </Button>
  );
};

/**
 * A Slack sign-up goes on to install okthx into the workspace, and that result
 * reaches this tab once it is signed in. Marking the connect as started here
 * lets this tab, and only this one, offer to add okthx to channels then.
 */
function startSlackSignIn() {
  dismissAllToasts();
  markConnectStarted("slack");
}

/** Sign in with Slack, as an icon door. */
const SlackSignInButton: React.FC = () => {
  const [location] = useLocation();

  return (
    <IconDoorTooltip label="Continue with Slack">
      <Button
        asChild
        variant="popover"
        size="icon"
        className={iconDoorClasses}
        data-statsig-name="auth-signin-slack"
      >
        {/* Dismiss stale toasts (e.g. "Sign-in failed") so they don't persist
            through the OAuth flow and confuse the user on a successful return. */}
        <a
          href={buildLoginAuthUrl(`${SLACK_SIGN_IN_PATH}/base`, location)}
          target="_blank"
          rel="noopener"
          onClick={startSlackSignIn}
        >
          <SlackIcon className="size-5" />
          <span className="sr-only">Continue with Slack</span>
        </a>
      </Button>
    </IconDoorTooltip>
  );
};

/**
 * Sign in with Apple. Styled like the other icon doors; the logo takes the
 * text color, which Apple's guidelines allow on a white button.
 */
const AppleSignInButton: React.FC = () => {
  const [location] = useLocation();

  return (
    <IconDoorTooltip label="Continue with Apple">
      <Button
        asChild
        variant="popover"
        size="icon"
        className={iconDoorClasses}
        data-statsig-name="auth-signin-apple"
      >
        {/* Dismiss stale toasts (e.g. "Sign-in failed") so they don't persist
            through the OAuth flow and confuse the user on a successful return. */}
        <a
          href={buildLoginAuthUrl(`${APPLE_SIGN_IN_PATH}/base`, location)}
          target="_blank"
          rel="noopener"
          onClick={dismissAllToasts}
        >
          <AppleLogoIcon className="size-5" />
          <span className="sr-only">Continue with Apple</span>
        </a>
      </Button>
    </IconDoorTooltip>
  );
};

const InAppBrowserNotice: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const url = window.location.href;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available in WebViews
      window.prompt("Copy this link:", url);
    }
  }, [url]);

  return (
    <div className="mx-auto flex flex-col items-center gap-4 text-center max-w-xs">
      <div className="flex items-center justify-center rounded-full bg-muted p-3">
        <ExternalLink className="size-6 text-muted-foreground" />
      </div>
      <p className="text-base leading-6">
        Sign-in is not supported in this browser. Please open this page in Safari or Chrome.
      </p>
      <p className="text-sm text-muted-foreground leading-5">
        Tap the <span className="font-semibold">&#x22EF;</span> menu above and select{" "}
        <span className="font-semibold">&ldquo;Open in Browser&rdquo;</span>
      </p>
      <Button
        variant="popover"
        className="border-default h-12 px-6 gap-2"
        onClick={handleCopy}
        data-statsig-name="auth-copy-link"
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        <span className="text-sm font-medium">{copied ? "Copied!" : "Copy Link"}</span>
      </Button>
    </div>
  );
};

/**
 * The phone door, when the gate says to offer it. `signupEnabled` is false
 * while the query is loading or failed, so the page never flashes a door that
 * turns out not to be open.
 */
const PhoneSignInButton: React.FC = () => {
  const { data } = useQuery(passwordlessOptionsQuery);
  const [location, setLocation] = useLocation();
  if (!data?.signupEnabled) return null;

  return (
    <Button
      variant="popover"
      className="border-default h-14 gap-2 px-8 py-2"
      onClick={() => setLocation(passwordlessDoorPath("/auth/phone", location))}
      data-statsig-name="auth-signin-phone"
    >
      <Phone className="size-4" />
      <span className="text-base font-medium leading-none">Continue with Phone</span>
    </Button>
  );
};

/** The WhatsApp door, as an icon door; it opens the WhatsApp sign-in page. */
const WhatsAppSignInButton: React.FC = () => {
  const [location, setLocation] = useLocation();

  return (
    <IconDoorTooltip label="Continue with WhatsApp">
      <Button
        variant="popover"
        size="icon"
        className={iconDoorClasses}
        onClick={() => setLocation(passwordlessDoorPath("/auth/whatsapp", location))}
        data-statsig-name="auth-signin-whatsapp"
      >
        <WhatsAppIcon className="size-5" />
        <span className="sr-only">Continue with WhatsApp</span>
      </Button>
    </IconDoorTooltip>
  );
};

/**
 * The doors after Google, Microsoft and Phone, as one row of round icons so
 * the page stays short as doors are added. Each waits on its own gate; the
 * gates read false while their queries load or fail, so the page never flashes
 * a door that turns out not to be open, and the divider goes when no door is.
 */
const OtherSignInOptions: React.FC = () => {
  const { data: apple } = useQuery(appleSignInOptionsQuery);
  const { data: slack } = useQuery(slackSignInOptionsQuery);
  const { data: passwordless } = useQuery(passwordlessOptionsQuery);
  const showApple = apple?.enabled === true;
  const showSlack = slack?.enabled === true;
  const showWhatsApp = passwordless?.whatsappSignupEnabled === true;
  if (!showApple && !showSlack && !showWhatsApp) return null;

  return (
    <>
      <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or continue with
        <span className="h-px flex-1 bg-border" />
      </div>
      {/* The sign-in page renders outside the app shell that provides tooltips. */}
      <TooltipProvider delayDuration={500} skipDelayDuration={0}>
        <div className="flex justify-center gap-3">
          {showApple && <AppleSignInButton />}
          {showSlack && <SlackSignInButton />}
          {showWhatsApp && <WhatsAppSignInButton />}
        </div>
      </TooltipProvider>
    </>
  );
};

const Footer = () => {
  return (
    <div className="py-6 text-sm text-muted-foreground text-center space-y-2">
      <div>
        Want to learn more? Visit{" "}
        <a href="https://okthx.com" className="link">
          okthx.com
        </a>
      </div>
    </div>
  );
};

export const Auth = () => {
  const inApp = useMemo(() => isInAppBrowser(), []);
  const [, setLocation] = useLocation();

  // Listen for OAuth result broadcasts (login success + login/connect errors)
  useEffect(() => {
    const channel = new BroadcastChannel("oauth_result");
    channel.onmessage = (
      event: MessageEvent<{
        provider: string;
        success: boolean;
        flow?: string;
        returnTo?: string;
        error?: string | null;
      }>,
    ) => {
      console.log("[Auth] OAuth broadcast received:", event.data);
      if (event.data.flow === "login" && event.data.success) {
        // Invalidate the currentUser query so AuthenticatedApp refetches with the new session cookie.
        // Without this, a stale 401 failure from before login can persist in the React Query cache,
        // causing the user to flash the home screen then get kicked back to the auth page.
        queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        const destination = resolveLoginReturnNavigation(event.data.returnTo);
        if (destination.kind === "external") {
          window.location.assign(destination.url);
          return;
        }
        setLocation(destination.path);
      } else if (!event.data.success) {
        errorToast(connectErrorCopy(event.data.error) ?? "Sign-in failed. Please try again.");
      }
    };
    return () => channel.close();
  }, [setLocation]);

  return (
    <div
      className={cn(
        "flex flex-col grow-1 items-center justify-center h-full",
        pageColumnClasses["8"],
      )}
    >
      <div className="flex flex-col grow-1 items-center justify-center md:max-w-full max-w-[688px]">
        <div className="flex flex-col gap-8 items-center w-full">
          <a
            href="https://okthx.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-3 items-center w-full"
          >
            <OkThxWordmark className="w-[136px] h-[31.98px]" />
            <span className="text-[28px] leading-8 font-serif italic text-center">
              Life coordinated
            </span>
          </a>
          {!inApp && (
            <span className="text-base leading-6 text-center mx-8">
              A coordination assistant that handles the actual work of scheduling
            </span>
          )}
        </div>
        <div className="mt-8 w-full max-w-[352px]">
          {inApp ? (
            <InAppBrowserNotice />
          ) : (
            <div className="flex flex-col gap-3">
              <SignInButton />
              {import.meta.env["VITE_SHOW_MICROSOFT_LOGIN"] === "true" && <MicrosoftSignInButton />}
              <PhoneSignInButton />
              <OtherSignInOptions />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
